// Custom Block Library — remote flag state (three independent fleet switches).
//
// Fetches a tiny JSON (VITE_BLOCK_LIBRARY_CONFIG_URL) like:
//   { "library_enabled": true, "ai_catalog_enabled": false, "optimizer_pool_enabled": false }
// and caches it so the synchronous gate can read it. The AI-catalog and optimizer-pool switches FAIL
// CLOSED (off) while unread/unreadable; the library (curated-only, safe) defaults ON when no remote is
// configured but is OFF when a configured flag is unreadable. Refresh interval lets a flip propagate
// without a redeploy. (Phase 2 can point this at the custom_block_library_flags table instead.)

export interface BlockLibraryFlagsState {
  configured: boolean;
  loaded: boolean;
  error: boolean;
  library: boolean;
  aiCatalog: boolean;
  optimizerPool: boolean;
}

let state: BlockLibraryFlagsState = { configured: false, loaded: false, error: false, library: true, aiCatalog: false, optimizerPool: false };
let started = false;
const subscribers = new Set<() => void>();
function notify() { for (const cb of subscribers) { try { cb(); } catch { /* ignore */ } } }
function url(): string | undefined { try { return import.meta.env.VITE_BLOCK_LIBRARY_CONFIG_URL || undefined; } catch { return undefined; } }

async function fetchOnce(): Promise<void> {
  const u = url();
  if (!u) { state = { configured: false, loaded: true, error: false, library: true, aiCatalog: false, optimizerPool: false }; notify(); return; }
  try {
    let signal: AbortSignal | undefined;
    try { signal = (AbortSignal as { timeout?: (ms: number) => AbortSignal }).timeout?.(4000); } catch { signal = undefined; }
    const res = await fetch(u, { cache: 'no-store', ...(signal ? { signal } : {}) });
    if (!res.ok) throw new Error('status ' + res.status);
    const j = (await res.json()) as Record<string, unknown>;
    state = { configured: true, loaded: true, error: false, library: j.library_enabled !== false, aiCatalog: j.ai_catalog_enabled === true, optimizerPool: j.optimizer_pool_enabled === true };
  } catch {
    // Unreadable → fail closed: library off, ai/optimizer off.
    state = { configured: true, loaded: true, error: true, library: false, aiCatalog: false, optimizerPool: false };
  }
  notify();
}

export function ensureBlockLibraryConfig(intervalMs = 60_000): void {
  if (started) return; started = true;
  const u = url();
  if (!u) { state = { configured: false, loaded: true, error: false, library: true, aiCatalog: false, optimizerPool: false }; return; }
  state = { configured: true, loaded: false, error: false, library: false, aiCatalog: false, optimizerPool: false }; // fail-closed until first read
  void fetchOnce();
  try { setInterval(() => void fetchOnce(), intervalMs); } catch { /* no timers */ }
}
export function getBlockLibraryFlagsState(): BlockLibraryFlagsState { return state; }
export function subscribeBlockLibraryConfig(cb: () => void): () => void { subscribers.add(cb); return () => subscribers.delete(cb); }
export function __setBlockLibraryFlagsStateForTest(s: BlockLibraryFlagsState): void { state = s; }
